import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { consumePushLaunch, splitLaunchUrl, type PushLaunch } from './push-launch';
import { retryPushSyncIfFailed, syncPushSubscription } from './web-push-client';

import { useCurrentUser } from '@/features/auth/use-auth';
import { markNotificationRead } from '@/features/notifications/notifications.api';
import { openWhatsAppConversation, pickUpWhatsAppCall } from '@/features/whatsapp/widget-bus';
import { registerAppNavigator } from '@/lib/app-navigation';

interface WorkerMessage {
  type?: unknown;
  url?: unknown;
}

/**
 * Glue between the push service worker and the signed-in app. Mounted once in
 * AppShell; renders nothing.
 *
 *   - Registers the router's navigate for code outside React (in-app
 *     navigation inside the installed app instead of `window.open`).
 *   - Acts on a notification tap: cold start (intent parked by main.tsx) and
 *     warm (the worker messages the already-open window).
 *   - Re-syncs this device's push subscription on every app open / user
 *     switch, so a shared device follows whoever is signed in.
 */
export const PushLaunchBridge = (): null => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const userId = useCurrentUser()?.id ?? null;

  useEffect(() => registerAppNavigator((to) => navigate(to)), [navigate]);

  const act = useCallback(
    (launch: PushLaunch): void => {
      if (launch.readNotificationId) {
        // Opening it from the phone is the same as "Open" on the popup card.
        markNotificationRead(launch.readNotificationId)
          .then(() => qc.invalidateQueries({ queryKey: ['notifications', 'me', 'inbox'] }))
          .catch(() => undefined); // Already read, or not ours (shared device).
      }
      if (launch.conversationId) openWhatsAppConversation(launch.conversationId);
      if (launch.callId) {
        pickUpWhatsAppCall(launch.callId, { answer: launch.answerCall === true });
      }
    },
    [qc],
  );

  // Cold start: whatever main.tsx lifted off the launch URL.
  useEffect(() => {
    if (!userId) return;
    const launch = consumePushLaunch();
    if (launch) act(launch);
  }, [userId, act]);

  // Warm: a tap while the app is already open.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    const container = navigator.serviceWorker;
    const onMessage = (event: MessageEvent<WorkerMessage | null>): void => {
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'utazzo-push-resync') {
        void syncPushSubscription();
        return;
      }
      if (data.type !== 'utazzo-open' || typeof data.url !== 'string') return;
      const { path, launch } = splitLaunchUrl(data.url);
      // Chat / call alerts point at "/" - the widget floats over every page,
      // so stay where the user is instead of bouncing them to the dashboard.
      const widgetOnly = path === '/' && Boolean(launch.conversationId || launch.callId);
      if (!widgetOnly) navigate(path);
      act(launch);
    };
    container.addEventListener('message', onMessage);
    // Messages queue until the page opts in; addEventListener alone doesn't.
    container.startMessages();
    return () => container.removeEventListener('message', onMessage);
  }, [navigate, act]);

  useEffect(() => {
    if (userId) void syncPushSubscription();
  }, [userId]);

  // A sync that failed on a flaky network (phone in a lift, laptop waking
  // up) is retried when the connection or the app comes back.
  useEffect(() => {
    if (!userId) return;
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') retryPushSyncIfFailed();
    };
    window.addEventListener('online', retryPushSyncIfFailed);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', retryPushSyncIfFailed);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [userId]);

  return null;
};
